/* STEP: Review & Commit (D) — generate the rate + TAT CSV files, download & review,
   then Commit to ProShip. Commit is gated until generated + the review checkbox is
   ticked. Commit runs the REAL backend sequence, each step awaited with progress:
     (a) createCourier (full payload incl. courier attributes)
     (b) PCIS/Direct-Integration admin API, only when the Click Post / Direct Integration
         step picked DIRECT — createCourierPartner (new-parent flow: registers the
         courier partner itself, credentials optional) or createCourierAccount
         (existing-parent flow: attaches an account to an already-registered partner)
     (c) serviceability lanes — dry-run (confirm:false) then confirm:true per lane
     (d) TAT per-zone CSV (primary service type) via tools/upload/servicability
     (e) rate card (19-col CSV) via tools/upload/servicability WEIGHT_PRICE
     (f) QC config (reverse-with-QC only, standard mode)
   On failure it stops, toasts, and keeps completed steps in W.commitDone so a
   retry resumes where it left off. */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import CheckIcon from '@mui/icons-material/Check'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import {useToast} from '../../components'
import {genRateCsv} from '../data'
import {QC_PARAMS} from '../../dummyData'
import {apiError} from '../../supplyMeta'
import {resolveCourierSchemaKey, getCourierSchema, diServiceType} from '../diCourierSchemas'
import {
  useCreateCourierMutation,
  useCreateCourierPartnerMutation,
  useUploadServiceabilityMutation,
  useUploadServicabilityFileMutation,
  useSaveQcConfigMutation,
} from '../../../../redux/supplyApiSlice'

/* lane → backend pincodeType. Reverse lanes: warehouse drop → DROP_PREPAID,
   end-customer pickup → PICKUP_PREPAID; the exchange lane has no backend type yet. */
const FWD_LANES = [
  ['pickup', 'PICKUP_PREPAID', 'Pickup serviceability'],
  ['dropPrepaid', 'DROP_PREPAID', 'Drop — Prepaid'],
  ['dropCod', 'DROP_COD', 'Drop — COD'],
]
const REV_LANES = [
  ['dropWarehousePrepaid', 'DROP_PREPAID', 'Drop (Warehouse) — Prepaid'],
  ['pickupEndPrepaid', 'PICKUP_PREPAID', 'Pickup (End Customer) — Prepaid'],
]

const yn = (v) => v === 'Yes'
const num = (v) => Number(v)

function courierPayload(W) {
  const D = W.D
  const S = W.S
  const DI = W.DI
  const a = D.attrs || {}
  return {
    cpAccountCode: D.code,
    name: D.display,
    prozoId: D.prozo,
    /* new parent: cp_id comes from the Click Post / Direct Integration step (DI.cpId,
       only for Click Post). existing parent: that step is gone, so it comes from the
       CP Id field restored on the Account Code step (D.cpId). */
    cpId: W.parentMode === 'new' ? (DI.mode === 'CLICKPOST' ? num(DI.cpId) : undefined) : num(D.cpId),
    email: D.email,
    contactNo: D.contactNo,
    parentId: W.parentRef.id,
    parent: W.parentRef.name,
    type: S.type || (S.direction === 'Reverse' ? 'RVP' : 'FORWARD'),
    dispatchMode: S.dispatchMode || (S.mode === 'Air' ? 'AIR' : 'SURFACE'),
    supported_shipment: a.supportedShipmentType || 'B2C', // model's JSON key is snake_case (@JsonProperty)
    isCod: !!S.isCod,
    isPrepaid: !!S.isPrepaid,
    integration: a.integration,
    serviceTypePrimary: D.serviceTypePrimary || (D.serviceTypes || [])[0],
    serviceTypeSecondary: D.serviceTypeSecondary || undefined,
    qcType: S.qc ? 'doorstep' : undefined,
    minChargeableWeight: num(a.minChargeableWeight),
    maxWeightSinglePiece: num(a.maxWeightSinglePiece),
    maxWeightPerShipment: num(a.maxWeightPerShipment),
    volumetricDivisor: num(a.volumetricDivisor),
    maxLength: num(a.maxLength),
    maxWidth: num(a.maxWidth),
    maxHeight: num(a.maxHeight),
    maxGirth: num(a.maxGirth),
    maxLwhSum: num(a.maxLwhSum),
    chargeableWeightRule: a.chargeableWeightRule,
    multiPieceAllowed: yn(a.multiPieceAllowed),
    maxPiecesPerShipment: num(a.maxPiecesPerShipment),
    fragileHandlingAvailable: yn(a.fragileHandlingAvailable),
    specialPackagingRequired: yn(a.specialPackagingRequired),
    remarks: a.remarks || '',
    metroServiceable: yn(a.metroServiceable),
    nddEligible: yn(a.nddEligible),
    sddEligible: yn(a.sddEligible),
    forwardSlaMetroDays: num(a.forwardSlaMetroDays),
    forwardSlaNonMetroDays: num(a.forwardSlaNonMetroDays),
    pickupCutoffTime: a.pickupCutoffTime,
    pickupDays: a.pickupDays,
    maxDeliveryAttempts: num(a.maxDeliveryAttempts),
    deliveryDays: a.deliveryDays,
    realTimeTracking: yn(a.realTimeTracking),
    trackingType: a.trackingType,
    onboardedDate: a.onboardedDate,
  }
}

/* `credentials` is built dynamically from the resolved courier schema (diCourierSchemas.js)
   since every courier has a different field set — see StepMode.js for how schemaKey is
   resolved from the parent courier + direction already captured earlier. */
function diCredentials(W) {
  const DI = W.DI
  const parentName = W.parentMode === 'new' ? W.A.name || '' : W.D.parentId || ''
  const schema = getCourierSchema(resolveCourierSchemaKey(parentName, W.S.direction))
  const fields = DI.fields || {}
  const credentials = {}
  schema.fields.forEach((f) => {
    credentials[f.key] = f.type === 'toggle' ? !!fields[f.key] : fields[f.key] || ''
  })
  /* user-added custom key/value pairs (StepMode "Additional fields") — merged on
     top of the schema fields. Both key and value are guaranteed non-blank there. */
  ;(DI.extras || []).forEach((x) => {
    const k = String(x.key || '').trim()
    if (k) credentials[k] = x.value
  })
  return credentials
}

/* PCIS courier-partners registration payload — used instead when onboarding a
   brand-new parent, since the courier doesn't exist in PCIS yet (nothing to
   attach an account to). cp_id is manually entered on Step 3 (no lookup possible
   for a courier that isn't registered anywhere yet); credentials are optional. */
function diPartnerPayload(W) {
  const D = W.D
  const DI = W.DI
  return {
    courier_code: DI.partnerCode,
    courier_parent: DI.partnerCode,
    service_type: diServiceType(W.S.direction),
    display_name: D.display,
    cp_id: num(DI.cpId),
    is_active: true,
    credentials: diCredentials(W),
  }
}

export default function StepReview({W, dispatch, idx, goBack}) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [prog, setProg] = useState([])

  const [createCourier] = useCreateCourierMutation()
  const [createCourierPartner] = useCreateCourierPartnerMutation()
  const [uploadServiceability] = useUploadServiceabilityMutation()
  const [uploadServicabilityFile] = useUploadServicabilityFileMutation()
  const [saveQcConfig] = useSaveQcConfigMutation()

  const hasRates = !!((W.COMM.matrix && W.COMM.matrix.length) || (W.AI && W.AI.rates))
  const parentName = (W.parentRef && W.parentRef.name) || (W.parentMode === 'new' ? W.A.name || '(new parent)' : W.D.parentId || '—')
  const rateSrc = W.COMM.method === 'ai' ? 'AI extraction' + (W.AI.edits ? ' · ' + W.AI.edits + ' edits' : '') : 'Manual upload' + (W.COMM.edits ? ' · ' + W.COMM.edits + ' edits' : '')
  const canCommit = !busy

  /* build the ordered task list — each task is independently resumable */
  const buildTasks = () => {
    const tasks = []
    tasks.push({
      key: 'courier',
      label: 'Create courier account code (' + W.D.code + ')',
      run: () => createCourier(courierPayload(W)).unwrap(),
    })
    /* NEW-parent + Direct Integration only — registers the courier partner itself
       (it doesn't exist in PCIS yet). The existing-parent DI flow (attaching an
       account via courier-accounts) has been removed from onboarding — that's now
       done from PCIS Health → Manage (Add). */
    if (W.DI.mode === 'DIRECT' && W.parentMode === 'new') {
      tasks.push({
        key: 'diPartner',
        label: 'Register Direct Integration courier partner (' + W.DI.partnerCode + ')',
        run: () => createCourierPartner(diPartnerPayload(W)).unwrap(),
      })
    }
    const reverse = W.S.direction === 'Reverse'
    const lanes = reverse ? REV_LANES : FWD_LANES
    lanes.forEach(([laneKey, pincodeType, label]) => {
      if (!W.SVC[laneKey]) return // lane not provided (e.g. reverse auto-fetch)
      tasks.push({
        key: 'svc_' + laneKey,
        label: 'Serviceability — ' + label,
        run: async () => {
          const file = (W.SVCFILES || {})[laneKey]
          if (!(file instanceof File)) {
            throw new Error('File for "' + label + '" is no longer attached (files are not kept in saved drafts) — go back to the Serviceability step and re-attach it.')
          }
          const args = {file, courierAccountCodes: [W.D.code], courierParent: parentName, pincodeType}
          await uploadServiceability({...args, confirm: false}).unwrap() // dry-run validation
          await uploadServiceability({...args, confirm: true}).unwrap()
        },
      })
    })
    if (hasRates) {
      tasks.push({
        key: 'rates',
        label: 'Upload rate card',
        run: () => {
          const file = new File([genRateCsv(W)], 'rates.csv', {type: 'text/csv'})
          return uploadServicabilityFile({prozoId: W.D.prozo, type: 'WEIGHT_PRICE', file}).unwrap()
        },
      })
    }
    if (W.S.qc && (W.QC.mode || 'standard') === 'standard' && W.QC.stdParams.length) {
      tasks.push({
        key: 'qc',
        label: 'Save QC configuration',
        run: () => {
          const qcQuestionList = W.QC.stdParams.map((en) => {
            const pm = QC_PARAMS.find((p) => p.en === en)
            return {question: en, is_mandatory: 1, value: (pm && pm.qid) || ''}
          })
          return saveQcConfig({prozoId: W.D.prozo, qcConfig: {status: true, categoryBasedQcLists: [{category: 'STANDARD', qcQuestionList}]}}).unwrap()
        },
      })
    }
    return tasks
  }

  const commit = async () => {
    if (!W.parentRef || !W.parentRef.id) {
      toast('Parent reference missing — go back to the Parent step and select/create the parent.', 'error')
      return
    }
    if (W.S.direction === 'Reverse' && W.SVC.pickupEndExchange) {
      toast('Exchange lane not yet supported by the backend — skipping its upload.', 'info')
    }
    const done = {...(W.commitDone || {})}
    const tasks = buildTasks()
    setBusy(true)
    setProg(tasks.map((t) => ({key: t.key, label: t.label, state: done[t.key] ? 'done' : 'pending'})))
    for (const t of tasks) {
      if (done[t.key]) continue
      setProg((s) => s.map((x) => (x.key === t.key ? {...x, state: 'running'} : x)))
      try {
        await t.run()
      } catch (e) {
        const msg = e instanceof Error && !e.response ? e.message : apiError(e)
        setProg((s) => s.map((x) => (x.key === t.key ? {...x, state: 'error', msg} : x)))
        toast(t.label + ' failed: ' + msg, 'error')
        setBusy(false)
        return // completed steps stay in W.commitDone — retry resumes here
      }
      done[t.key] = true
      dispatch({type: 'FIELD', slice: 'commitDone', key: t.key, value: true})
      setProg((s) => s.map((x) => (x.key === t.key ? {...x, state: 'done'} : x)))
    }
    setBusy(false)
    dispatch({type: 'SET', slice: 'committedParentId', value: W.parentRef.id})
    toast('Account code committed to ProShip. It now appears in the directory.', 'success')
    dispatch({type: 'STEP', step: idx('done')})
  }

  const stateIcon = (st) =>
    st === 'done' ? (
      <CheckIcon style={{width: 15, height: 15, color: 'var(--success)'}} />
    ) : st === 'error' ? (
      <ErrorOutlineIcon style={{width: 15, height: 15, color: 'var(--danger, #C62828)'}} />
    ) : st === 'running' ? (
      <HourglassEmptyIcon style={{width: 15, height: 15, color: 'var(--primary)'}} />
    ) : (
      <HourglassEmptyIcon style={{width: 15, height: 15, color: 'var(--neutral)'}} />
    )

  return (
    <div className='card card-pad' style={{maxWidth: 1400}}>
      <h3 style={{margin: '0 0 4px'}}>Review &amp; Commit</h3>
      <p className='sub' style={{margin: '0 0 14px'}}>
        Generate the rate and TAT files, download and review them, then commit to ProShip. The system generates exactly what you entered — your inputs, your review.
      </p>

      <div className='card' style={{background: '#FAFBFC', marginBottom: 14}}>
        <div className='card-pad'>
          <div className='dl' style={{display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 18px'}}>
            <dt>Account code</dt>
            <dd>
              <b>{W.D.code || '—'}</b>
            </dd>
            <dt>Display name</dt>
            <dd>{W.D.display || '—'}</dd>
            <dt>Prozo ID · Email</dt>
            <dd>{(W.D.prozo || '—') + ' · ' + (W.D.email || '—')}</dd>
            <dt>Integration</dt>
            <dd>{W.DI.mode === 'DIRECT' ? 'Direct Integration (' + (W.DI.partnerCode || '—') + ')' : W.DI.mode === 'CLICKPOST' ? 'Click Post (CP Id ' + (W.DI.cpId || '—') + ')' : '—'}</dd>
            <dt>Parent</dt>
            <dd>{parentName}</dd>
            <dt>Direction · Mode</dt>
            <dd>
              {(W.S.direction || '—') + (W.S.mode ? ' / ' + W.S.mode : '')}
              {W.S.qc ? ' · QC at doorstep' : ''}
            </dd>
            <dt>Payment</dt>
            <dd>{[W.S.isPrepaid && 'Prepaid', W.S.isCod && 'COD'].filter(Boolean).join(' + ') || '—'}</dd>
            <dt>Service types</dt>
            <dd>
              {W.D.serviceTypePrimary ? (
                <>
                  <b>{W.D.serviceTypePrimary}</b> (primary)
                  {W.D.serviceTypeSecondary ? ' · ' + W.D.serviceTypeSecondary + ' (secondary)' : ''}
                </>
              ) : (
                '—'
              )}
            </dd>
            <dt>Serviceability</dt>
            <dd>
              {(W.S.direction === 'Reverse' ? REV_LANES : FWD_LANES)
                .map(([k, , label]) => (W.SVC[k] ? label.split(' — ')[0] + ' ✓' : null))
                .filter(Boolean)
                .join(' · ') || 'No files attached'}
              {W.S.direction === 'Reverse' && W.SVC.pickupEndExchange ? ' · Exchange (skipped — not supported)' : ''}
            </dd>
            <dt>QC params</dt>
            <dd>{W.S.qc ? (W.QC.stdParams || []).length + ' selected' : 'N/A (no doorstep QC)'}</dd>
            <dt>Rate source</dt>
            <dd>{hasRates ? rateSrc : 'No rate card — upload later via Commercials'}</dd>
          </div>
        </div>
      </div>


      {prog.length ? (
        <div className='card' style={{background: '#FAFBFC', marginTop: 14}}>
          <div className='card-pad' style={{paddingTop: 10, paddingBottom: 10}}>
            <div className='seclabel' style={{marginBottom: 8}}>
              Commit progress
            </div>
            {prog.map((p) => (
              <div key={p.key} style={{display: 'flex', alignItems: 'flex-start', gap: 8, padding: '4px 0', fontSize: 12.5}}>
                <span style={{marginTop: 1}}>{stateIcon(p.state)}</span>
                <div style={{minWidth: 0}}>
                  <span style={{fontWeight: p.state === 'running' ? 700 : 500}}>{p.label}</span>
                  {p.state === 'error' && p.msg ? (
                    <div className='muted' style={{fontSize: 11.5, color: 'var(--danger, #C62828)'}}>
                      {p.msg}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className='row gap12' style={{display: 'flex', gap: 12, marginTop: 16, justifyContent: 'flex-end'}}>
        <button type='button' className='btn' disabled={busy} onClick={goBack}>
          ← Back
        </button>
        <button type='button' className='btn btn-primary' disabled={!canCommit} onClick={commit}>
          <CheckIcon style={{width: 16, height: 16}} />
          {busy ? 'Committing…' : Object.keys(W.commitDone || {}).length ? 'Retry commit' : 'Commit to ProShip'}
        </button>
      </div>
    </div>
  )
}

StepReview.propTypes = {
  W: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  idx: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
}
